import {type ChangeEvent, type FormEvent, useCallback, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {useAuth} from 'UI/hooks/useAuth';

/** Der Status des Namen-Bearbeiten-Formulars. */
export type UserNameEditorStatus = 'error' | 'idle' | 'required' | 'submitting';

/**
 * Hook für das inline bearbeitbare Namensfeld auf der Account-Karte der Settings-Seite. Kapselt
 * den `useAuth()`-Zugriff, damit die Komponente selbst nur diesen einen Hook aufrufen muss.
 *
 * @returns Bearbeitungs-State und die zugehörigen Handler.
 */
export const useUserNameEditor = () => {
    const {t} = useTranslation();
    const {updateName, user} = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState('');
    const [status, setStatus] = useState<UserNameEditorStatus>('idle');

    /** Wechselt in den Bearbeiten-Modus, vorbefüllt mit dem aktuellen Namen. */
    const startEditing = useCallback(() => {
        setName(user?.name ?? '');
        setStatus('idle');
        setIsEditing(true);
    }, [user]);

    /** Bricht die Bearbeitung ohne Speichern ab. */
    const cancelEditing = useCallback(() => {
        setIsEditing(false);
        setStatus('idle');
    }, []);

    const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    }, []);

    const handleSubmit = useCallback((e: FormEvent) => {
        e.preventDefault();

        const trimmedName = name.trim();

        if (!trimmedName) {
            setStatus('required');

            return;
        }

        setStatus('submitting');

        /** Speichert den neuen Namen über `useAuth().updateName`. */
        const submit = async () => {
            try {
                await updateName(trimmedName);
                setIsEditing(false);
                setStatus('idle');
            } catch {
                setStatus('error');
            }
        };

        void submit();
    }, [name, updateName]);

    return {
        cancelEditing,
        displayName: user?.name ?? t('account.defaultUser'),
        handleNameChange,
        handleSubmit,
        isEditing,
        name,
        startEditing,
        status,
        t
    };
};